import type { Order, OrderStatus } from '@shopgnd/shared';
import type { ShopgndApiConfig } from '../config';
import { fetchWithAuth } from '../config';
import { mapAmplifyOrder } from './mapOrder';

export type AdminOrdersPageParams = {
  limit?: number;
  cursor?: string;
  status?: OrderStatus | 'all';
};

export type AdminOrdersPage = {
  orders: Order[];
  nextCursor?: string;
  hasMore: boolean;
};

const CUSTOM_URL_KEY = 'adminOrdersUrl';

export async function listAdminOrdersPage(
  config: ShopgndApiConfig,
  params: AdminOrdersPageParams = {},
): Promise<AdminOrdersPage> {
  const base = await config.getCustomUrl(CUSTOM_URL_KEY);
  if (!base) {
    throw new Error('Admin orders API is not configured. Deploy the Amplify backend.');
  }

  const limit = Math.min(Math.max(params.limit ?? 50, 1), 100);
  const qs = new URLSearchParams({ limit: String(limit) });
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.status && params.status !== 'all') qs.set('status', params.status);

  const res = await fetchWithAuth(config, `${base}?${qs.toString()}`, { method: 'GET' });
  const payload = (await res.json().catch(() => ({}))) as AdminOrdersPage & { error?: string };
  if (!res.ok) {
    throw new Error(payload.error ?? `Admin orders request failed (${res.status})`);
  }

  return {
    orders: (payload.orders ?? []).map((o) =>
      mapAmplifyOrder(o as unknown as Record<string, unknown>),
    ),
    nextCursor: payload.nextCursor,
    hasMore: Boolean(payload.hasMore),
  };
}
